import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import legalDocsApi from '@/api/legalDocuments';

/**
 * TermsGateProvider — dono do status do portão de aceite da conta.
 *
 * O status mora aqui, e não dentro do modal, porque dois consumidores muito
 * diferentes precisam da mesma resposta:
 *
 *  - `TermsAcceptanceGate` desenha o overlay bloqueante;
 *  - os tours guiados (`Spotlight`, `TutorialsLauncher`) precisam ficar fora do
 *    caminho até o aceite. O overlay do tour é `z-[1000]` e o portão era
 *    `z-[200]`: o tour desenhava POR CIMA dos termos, iluminando elementos que
 *    o usuário não conseguia clicar e pedindo uma ação impossível.
 *
 * `blocked` também é verdadeiro enquanto o status ainda é desconhecido para um
 * admin. Começar o tour e arrancá-lo meio segundo depois é a mesma sobreposição,
 * só que mais curta. Quem não é admin nunca consulta o portão e nunca é
 * bloqueado — o aceite vale para a conta e só admin pode registrá-lo.
 */

const TermsGateContext = createContext(null);

// Fora do provider (rotas irmãs, testes de unidade de um tour) não há portão a
// respeitar. Identidade estável para não invalidar memos de quem consome.
const NO_GATE = Object.freeze({
    blocked: false,
    checking: false,
    pending: [],
    account: null,
    current: null,
    acceptCurrent: () => {},
});

export function TermsGateProvider({ children }) {
    const { isAdmin, isLoading } = useAuth();
    const admin = !isLoading && !!isAdmin?.();

    const [pending, setPending] = useState([]);
    const [account, setAccount] = useState(null);
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        if (!admin) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { pending: pend, account: acc } = await legalDocsApi.getGateStatus();
                if (!cancelled) {
                    setPending(pend || []);
                    setAccount(acc || null);
                }
            } catch {
                // Falha na consulta não pode prender a plataforma: sem resposta,
                // não há pendência conhecida e a navegação (e os tours) seguem.
                if (!cancelled) setPending([]);
            } finally {
                if (!cancelled) setChecked(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [admin]);

    // Consome o item aceito. O overlay avança para o próximo documento pendente
    // (ou some, liberando os tours) sem uma nova ida ao servidor.
    const acceptCurrent = useCallback(() => setPending((prev) => prev.slice(1)), []);

    const value = useMemo(() => {
        const checking = isLoading || (admin && !checked);
        return {
            blocked: checking || pending.length > 0,
            checking,
            pending,
            account,
            current: pending[0] || null,
            acceptCurrent,
        };
    }, [isLoading, admin, checked, pending, account, acceptCurrent]);

    return <TermsGateContext.Provider value={value}>{children}</TermsGateContext.Provider>;
}

export function useTermsGate() {
    return useContext(TermsGateContext) || NO_GATE;
}

export default TermsGateProvider;
